import React, { useMemo, useState } from 'react';
import { useAimSnapshot, useAimRun } from './lib/aimQueries';
import { bucketFor } from './lib/ratingScale';
import { classifyVfi, computeVfiFromBreakdown, extractVfiBreakdown, vfiBreakdownRows } from './lib/vfi';
import { fmtDuration, ABSURD_DURATION_SECONDS } from './lib/duration';
import { prettifyCategory } from './lib/categoryLabel';
import TrendArrow from './primitives/TrendArrow';
import InfoTooltip from './primitives/InfoTooltip';
import { AimSkeletonBars } from './primitives/AimEmpty';
import {
  ResponsiveContainer, RadialBarChart, RadialBar, PolarGrid, PolarAngleAxis,
  RadarChart, Radar, PolarRadiusAxis,
} from 'recharts';

/* ─── 1A AKS Gauge ───────────────────────────────────────────────── */

function AksGauge({ clientId, runId, compareRunId }) {
  const { data: snap, loading } = useAimSnapshot(clientId, 'P1', '', runId);
  const useCompare = Boolean(compareRunId && compareRunId !== runId);
  const { data: compareSnap, loading: compareLoading } = useAimSnapshot(clientId, useCompare ? 'P1' : null, '', useCompare ? compareRunId : null);
  const [expanded, setExpanded] = useState(false);

  const aks = snap?.value_numeric != null ? Number(snap.value_numeric) : null;
  const breakdown = snap?.value_breakdown || {};
  const { hex } = bucketFor(aks);

  // No delta when the user explicitly opts out via "Compare to: — None —".
  // Falling back to the prior run made the arrow lie about what was being
  // compared (markup #69).
  const prevAks = useMemo(() => {
    if (!useCompare) return null;
    const v = compareSnap?.value_numeric;
    return v != null ? Number(v) : null;
  }, [useCompare, compareSnap]);

  const radarData = useMemo(() => {
    if (!breakdown) return [];
    const map = {
      mention_pct: 'Visibility',
      accuracy_pct: 'Accuracy',
      completeness_pct: 'Completeness',
      inv_hallucination_pct: 'Trust',
      citation_authority_pct: 'Authority',
    };
    return Object.entries(map).map(([k, label]) => ({
      axis: label,
      // Recharts treats null as a gap on the radar — the right visual signal
      // for "unmeasured" instead of silently collapsing the axis to 0.
      value: breakdown[k] != null ? Number(breakdown[k]) : null,
      isMeasured: breakdown[k] != null,
    }));
  }, [breakdown]);

  // Full skeleton only on first load (no value yet). On refetch we keep the
  // existing value visible and add a small inline spinner so the user can see
  // that new data is being fetched without the gauge collapsing.
  if (loading && aks == null) {
    return (
      <div className="h-40 flex flex-col items-center justify-center w-full animate-pulse" role="status" aria-label="Loading AKS">
        <div className="w-32 h-32 rounded-full bg-gray-200 dark:bg-gray-800" />
      </div>
    );
  }
  if (aks == null) {
    return (
      <div className="h-40 flex flex-col items-center justify-center w-full rounded-lg border border-dashed border-gray-300 dark:border-gray-700 px-4 text-center">
        <div className="text-xs font-medium text-gray-600 dark:text-gray-300">No AKS snapshot for this run</div>
        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
          Pick a more recent run, or wait for the daily snapshot to cover this one.
        </div>
      </div>
    );
  }

  // radarData is always length 5 (one entry per axis); isMeasured flags whether
  // that axis actually carried a value. When the snapshot has no value_breakdown
  // (degraded/incomplete runs, or the P1 backend gap) none are measured, so the
  // Breakdown view has nothing to plot — disable the toggle instead of showing
  // an empty radar.
  const hasBreakdown = radarData.some((d) => d.isMeasured);
  const showRadar = expanded && hasBreakdown;
  // Spin whenever EITHER the current-run snapshot OR the compare-run snapshot
  // is in-flight, so picking a Compare-to also surfaces a loading state.
  const isRefetching = loading || compareLoading;
  const refetchClass = isRefetching ? 'opacity-60 transition-opacity' : 'transition-opacity';
  const spinner = isRefetching ? (
    <svg
      className="w-3 h-3 animate-spin text-[#32B1FF]"
      viewBox="0 0 24 24"
      fill="none"
      aria-label="Fetching new AKS data"
      role="status"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  ) : null;

  return (
    <div className="flex flex-col items-center">
      {showRadar ? (
        /* Spider/radar replaces the gauge when toggled on */
        <div className={`w-full flex flex-col ${refetchClass}`} style={{ height: 220 }}>
          <div className="flex items-baseline justify-center gap-2 mb-2">
            <span className="text-2xl font-bold" style={{ color: hex }}>{aks.toFixed(1)}</span>
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">AKS</span>
            {spinner}
          </div>
          <div className="flex-1 min-h-0">
            <ResponsiveContainer>
              <RadarChart data={radarData} outerRadius="72%">
                <PolarGrid />
                <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                <Radar dataKey="value" stroke={hex} fill={hex} fillOpacity={0.3} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        /* Default: circular gauge */
        <div className={`relative w-40 h-40 ${refetchClass}`}>
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart
              innerRadius="80%"
              outerRadius="100%"
              data={[{ name: 'aks', value: aks, fill: hex }]}
              startAngle={90}
              endAngle={-270}
            >
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar dataKey="value" background cornerRadius={6} fill={hex} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-2xl font-bold" style={{ color: hex }}>{aks.toFixed(1)}</div>
            <div className="flex items-center gap-1">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">AKS</div>
              {prevAks != null && <TrendArrow current={aks} previous={prevAks} />}
              {spinner}
            </div>
          </div>
        </div>
      )}

      {/* Markup-comment #61: replace the tiny text link with a segmented
          Gauge/Breakdown toggle so the alternate view is obviously available.
          Each segment is forced to the same width for visual symmetry. */}
      <div className="mt-3 inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden text-xs" role="tablist" aria-label="AKS visualization">
        <button
          type="button"
          role="tab"
          aria-selected={!expanded}
          onClick={() => setExpanded(false)}
          // Active segment is tinted with the gauge's score color (bucketFor)
          // so the toggle reflects the AKS rating instead of a fixed blue.
          style={!expanded ? { backgroundColor: hex } : undefined}
          className={`w-24 py-1 text-center transition ${!expanded ? 'text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
        >
          Gauge
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={expanded}
          onClick={() => setExpanded(true)}
          disabled={!hasBreakdown}
          title={hasBreakdown ? undefined : 'Category breakdown unavailable for this run'}
          style={expanded ? { backgroundColor: hex } : undefined}
          className={`w-24 py-1 text-center border-l border-gray-200 dark:border-gray-700 transition ${expanded ? 'text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'} disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          Breakdown
        </button>
      </div>
    </div>
  );
}

/* ─── 1B VFI Badge ───────────────────────────────────────────────── */

function VfiBadge({ clientId, runId }) {
  const { data: snap, loading } = useAimSnapshot(clientId, 'vfi', '', runId);
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <div className="flex flex-col items-center gap-2 animate-pulse" role="status" aria-label="Loading VFI">
        <div className="h-9 w-28 rounded-full bg-gray-200 dark:bg-gray-800" />
        <div className="h-3 w-40 rounded bg-gray-200 dark:bg-gray-800" />
      </div>
    );
  }
  const computedCv = computeVfiFromBreakdown(snap?.value_breakdown);
  if (!snap || (snap.value_numeric == null && computedCv == null)) {
    // Two distinct empty cases need different copy. VFI is stddev/mean of
    // mention rates *across categories*, so it needs ≥2 categories. A
    // single-prompt manual run only ever covers one category and can never
    // produce a value — telling the user to "pick a newer run" is misleading
    // there, so call out the real reason instead.
    const bdRows = vfiBreakdownRows(snap?.value_breakdown);
    const singleCategory = bdRows.length === 1 ? bdRows[0].category : null;
    return (
      <div className="h-32 flex flex-col items-center justify-center w-full rounded-lg border border-dashed border-gray-300 dark:border-gray-700 px-4 text-center">
        {singleCategory ? (
          <>
            <div className="text-xs font-medium text-gray-600 dark:text-gray-300">VFI needs ≥2 query categories</div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
              This run only covered one (<strong>{prettifyCategory(singleCategory)}</strong>), so fragility can’t be measured.
            </div>
          </>
        ) : (
          <>
            <div className="text-xs font-medium text-gray-600 dark:text-gray-300">No VFI snapshot for this run</div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
              Pick a more recent run, or wait for the daily snapshot to cover this one.
            </div>
          </>
        )}
      </div>
    );
  }
  // Backend snapshots have stored the wrong scalar in value_numeric (avg_rate
  // instead of stddev/mean). Recompute from breakdown when available.
  const cv = computedCv != null
    ? computedCv
    : classifyVfi(snap.value_numeric).cv;
  const { color, severity, meterPct } = classifyVfi(cv);
  const { sorted, best, worst } = extractVfiBreakdown(snap?.value_breakdown);
  // No per-category breakdown (null value_breakdown on degraded/incomplete runs)
  // → the value + meter still render, but the expand affordance is dead, so drop
  // the click and show why instead of an interactive pill that does nothing.
  const hasBreakdown = sorted.length > 0;
  const bestPct = best && Number.isFinite(best.pct) ? best.pct : null;
  const worstPct = worst && Number.isFinite(worst.pct) ? worst.pct : null;
  const pillClass = 'inline-flex items-baseline gap-2 rounded-full px-4 py-2 border focus:outline-none';
  const pill = (
    <>
      <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">VFI</span>
      <span className="text-2xl font-bold" style={{ color }}>{cv.toFixed(2)}</span>
    </>
  );

  return (
    <div className="text-center w-full">
      {hasBreakdown ? (
        <button type="button" onClick={() => setExpanded((v) => !v)} className={pillClass} style={{ borderColor: color }}>
          {pill}
        </button>
      ) : (
        <span className={pillClass} style={{ borderColor: color }}>{pill}</span>
      )}
      <div className="mt-2 flex items-center justify-center gap-2">
        <div className="w-28 h-2 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
          <div className="h-full" style={{ width: `${meterPct}%`, backgroundColor: color }} />
        </div>
        <span className="text-[11px] font-medium" style={{ color }}>{severity}</span>
      </div>
      <div className="mt-2 text-[11px] text-gray-500 dark:text-gray-400 flex flex-wrap justify-center gap-x-3">
        {best && (
          <span>Best: <strong>{best.category_display || prettifyCategory(best.category)}</strong>{bestPct != null ? ` (${bestPct.toFixed(0)}%)` : ''}</span>
        )}
        {worst && (
          <span>Worst: <strong>{worst.category_display || prettifyCategory(worst.category)}</strong>{worstPct != null ? ` (${worstPct.toFixed(0)}%)` : ''}</span>
        )}
      </div>
      {!hasBreakdown && (
        <div className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">Breakdown unavailable for this run.</div>
      )}
      {expanded && sorted.length > 0 && (
        <div className="mt-3 space-y-1.5 text-left">
          {sorted.map((row) => {
            const pct = Number.isFinite(row.pct) ? row.pct : 0;
            return (
              <div key={row.category} className="flex items-center gap-2">
                <span className="text-xs w-32 truncate text-gray-700 dark:text-gray-300">{row.category_display || prettifyCategory(row.category)}</span>
                <div className="flex-1 h-2 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
                  <div
                    className="h-full"
                    style={{ width: `${Math.min(100, pct)}%`, backgroundColor: bucketFor(pct).hex }}
                  />
                </div>
                <span className="text-xs w-10 text-right text-gray-600 dark:text-gray-400">
                  {pct.toFixed(0)}%
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── 1C Run Status Card ─────────────────────────────────────────── */

function fmtRunDate(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function RunStatusCard({ clientId, runId }) {
  const { data: run, loading } = useAimRun(clientId, runId);

  const statusBadge = useMemo(() => {
    const s = run?.status;
    if (!s) return null;
    // Completed runs are green when clean and yellow when the run logged errored
    // responses — so the badge colour alone flags a not-quite-clean run.
    if (s === 'completed') {
      const errors = Number(run?.total_errors) || 0;
      return {
        color: errors > 0
          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
          : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
        label: 'Completed',
      };
    }
    if (s === 'running') return { color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', label: '⏳ Running' };
    if (s === 'failed') return { color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', label: '❌ Failed' };
    return { color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', label: s };
  }, [run]);

  if (loading) {
    return (
      <div role="status" aria-label="Loading run">
        <AimSkeletonBars lines={4} />
      </div>
    );
  }
  if (!run) {
    return (
      <div className="h-32 flex flex-col items-center justify-center w-full rounded-lg border border-dashed border-gray-300 dark:border-gray-700 px-4 text-center">
        <div className="text-xs font-medium text-gray-600 dark:text-gray-300">No run selected</div>
        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
          Pick a run from the dropdown above to view its status.
        </div>
      </div>
    );
  }

  const runType = run.run_type_display || prettifyCategory(run.run_type) || 'Run';

  return (
    <div className="text-sm">
      <div className="flex items-center gap-2 mb-2 max-[499px]:flex-col max-[499px]:items-start max-[499px]:gap-1">
        <span className="font-semibold text-gray-900 dark:text-white max-[499px]:w-full">{runType}</span>
        <span className="text-gray-400 max-[499px]:hidden">·</span>
        <span className="text-gray-700 dark:text-gray-300 max-[499px]:w-full">{fmtRunDate(run.run_date)}</span>
        {statusBadge && (
          <span className={`ml-auto text-xs px-2 py-0.5 rounded-full max-[499px]:ml-0 max-[499px]:w-full max-[499px]:text-center ${statusBadge.color}`}>{statusBadge.label}</span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-y-1 gap-x-6 text-xs text-gray-700 dark:text-gray-300">
        <div className="flex items-baseline justify-between">
          <span className="font-semibold text-gray-600 dark:text-gray-300">Calls:</span>
          <span className="tabular-nums">{run.total_api_calls ?? 0}</span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="font-semibold text-gray-600 dark:text-gray-300">Error{Number(run.total_errors) === 1 ? '' : 's'}:</span>
          <span className={`tabular-nums ${Number(run.total_errors) > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}>{run.total_errors ?? 0}</span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="font-semibold text-gray-600 dark:text-gray-300">Duration:</span>
          <span
            className={`tabular-nums ${Number(run.duration_seconds) > ABSURD_DURATION_SECONDS ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}
            title={Number(run.duration_seconds) > ABSURD_DURATION_SECONDS ? 'Run exceeded 24h without completing — likely stuck or orphaned.' : undefined}
          >
            {fmtDuration(run.duration_seconds)}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── Composite ──────────────────────────────────────────────────── */

function HeadlineCard({ title, subtitle, tooltip, children, className = '' }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 h-full flex flex-col">
      <div className="mb-3">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
          {tooltip && <InfoTooltip label={title}>{tooltip}</InfoTooltip>}
        </div>
        {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
      </div>
      <div className={`flex-1 ${className}`}>{children}</div>
    </div>
  );
}

export default function HeadlineMetrics({ clientId, runId, compareRunId }) {
  return (
    <div className="grid grid-cols-1 min-[780px]:grid-cols-2 min-[1200px]:grid-cols-3 gap-4">
      <HeadlineCard
        title="AI Knowledge Score"
        subtitle={
          <>
            <InfoTooltip
              label="P1 — AI Knowledge Score"
              align="left"
              trigger={
                <span className="border-b border-dotted border-gray-400 dark:border-gray-500 cursor-help">P1</span>
              }
            >
              <strong>P1 — AI Knowledge Score.</strong> A 0–100 composite of how well AI assistants know your property.
              Weighted from five sub-scores: <strong>Visibility</strong> (30%),{' '}
              <strong>Accuracy</strong> (25%), <strong>Completeness</strong> (20%),{' '}
              <strong>Trust</strong> — freedom from hallucinations (15%), and{' '}
              <strong>Authority</strong> — citations to your own site (10%).
              Higher is better; switch to the <em>Breakdown</em> tab for the per-axis radar.
            </InfoTooltip>
            <span> — overall AI knowledge of your property</span>
          </>
        }
      >
        <AksGauge clientId={clientId} runId={runId} compareRunId={compareRunId} />
      </HeadlineCard>
      <HeadlineCard title="Visibility Fragility Index" subtitle="VFI — how evenly AI mentions are spread across your categories. Low = consistent coverage; high = AI focuses on a few and ignores others." className="flex items-center justify-center min-h-[160px]">
        <VfiBadge clientId={clientId} runId={runId} />
      </HeadlineCard>
      <div className="min-[780px]:col-span-2 min-[1200px]:col-span-1 h-full">
        <HeadlineCard title="Run Status" subtitle="Health of the selected AI Monitor run — provider call counts, errors, and total runtime.">
          <RunStatusCard clientId={clientId} runId={runId} />
        </HeadlineCard>
      </div>
    </div>
  );
}
